import '@babel/polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';

import { LanguageCode } from '@tink/config/src/markets';
import { LanguageProvider } from '@tink/core/src/components/providers/LanguageProvider/LanguageProvider';
import { Version } from './models/version';
import { VersionProvider } from './components/ApplicationProviders/VersionProvider/VersionProvider';
import { App } from './components/App/App';
import { unregister as unregisterServiceWorker } from './registerServiceWorker';
import { loadIntlPolyfill } from './intlPolyfill';
import { ConfigurationProvider } from './components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { ConfiguredThemeProvider } from './components/ApplicationProviders/ConfiguredThemeProvider/ConfiguredThemeProvider';
import { initLogger } from './utilities/logger';
import { store } from './stores/redux-store';
import { UrlQueryProvider } from './components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { ApplicationErrorBoundary } from './components/ApplicationErrorBoundary/ApplicationErrorBoundary';
import { AnalyticsProvider } from './core/Analytics/AnalyticsProvider';
import { TinkLinkAnalytics } from './core/Analytics/tink-link-analytics';
import { HandoffExchange } from './core/components/HandoffExchange/HandoffExchange';
import { englishMessages } from './i18n';
import { VERSIONS } from './config';
import { OverlayProvider } from './components/ApplicationProviders/OverlayProvider/OverlayProvider';

initLogger();

const versionRegex = VERSIONS.join('|');

const start = () => {
  ReactDOM.render(
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <Provider store={store}>
        <Switch>
          <Route path={`/:version(${versionRegex})/handoff/:handoffSessionId`}>
            <LanguageProvider defaultLocale={LanguageCode.en} messages={englishMessages}>
              <ConfigurationProvider>
                <ConfiguredThemeProvider>
                  <AnalyticsProvider initialTinklinkAnalytics={new TinkLinkAnalytics()}>
                    <OverlayProvider>
                      <VersionProvider initialVersion={new Version('1.0')}>
                        <ApplicationErrorBoundary>
                          <HandoffExchange />
                        </ApplicationErrorBoundary>
                      </VersionProvider>
                    </OverlayProvider>
                  </AnalyticsProvider>
                </ConfiguredThemeProvider>
              </ConfigurationProvider>
            </LanguageProvider>
          </Route>

          <Route>
            <UrlQueryProvider>
              <ConfigurationProvider>
                <ConfiguredThemeProvider>
                  <App />
                </ConfiguredThemeProvider>
              </ConfigurationProvider>
            </UrlQueryProvider>
          </Route>
        </Switch>
      </Provider>
    </BrowserRouter>,
    document.getElementById('root') as HTMLElement
  );
};

async function init() {
  await loadIntlPolyfill();
  start();
}

init();

unregisterServiceWorker();
